import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle, XCircle, Clock } from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { verifyCashfreeOrder } from "../lib/cashfree";

export default function PaymentReturnPage() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const orderId = params.get("order_id") || "";
  const [state, setState] = useState({ status: "verifying", kind: null, entity: null });

  useEffect(() => {
    if (!orderId) {
      setState({ status: "error", kind: null, entity: null });
      return;
    }
    verifyCashfreeOrder(orderId)
      .then((data) => {
        const paid = (data.order_status || "").toUpperCase() === "PAID";
        setState({
          status: paid ? "paid" : (data.order_status || "pending").toLowerCase(),
          kind: data.kind,
          entity: data.entity,
        });
        if (paid) toast.success("Payment successful — booking confirmed!");
      })
      .catch(() => setState({ status: "error", kind: null, entity: null }));
  }, [orderId]);

  const goToBookings = () => nav(state.kind === "rental" ? "/my-rentals" : "/bookings");

  return (
    <div className="max-w-lg mx-auto px-6 py-16 text-center" data-testid="payment-return">
      {state.status === "verifying" && (
        <>
          <div className="w-14 h-14 border-4 border-gold border-t-transparent rounded-full animate-spin inline-block mb-6" />
          <h1 className="font-serif text-3xl text-midnight mb-3">Verifying payment…</h1>
          <p className="text-sm text-neutral-600">Please wait, do not close this window.</p>
        </>
      )}

      {state.status === "paid" && (
        <>
          <div className="w-16 h-16 rounded-full bg-emerald-100 mx-auto mb-6 flex items-center justify-center">
            <CheckCircle size={48} weight="fill" className="text-emerald-600" />
          </div>
          <p className="text-xs tracking-[0.3em] uppercase text-emerald-700 mb-2">Payment Successful</p>
          <h1 className="font-serif text-4xl text-midnight mb-3">Your {state.kind === "rental" ? "rental" : "booking"} is confirmed</h1>
          <p className="text-sm text-neutral-600 mb-8">A confirmation email is on its way. You can view your booking anytime from the menu.</p>
          <Button onClick={goToBookings} className="bg-midnight text-bone hover:bg-midnight-hover px-8 py-6" data-testid="pr-view-btn">
            View my {state.kind === "rental" ? "rentals" : "bookings"}
          </Button>
        </>
      )}

      {["pending", "active", "expired", "cancelled", "failed", "error"].includes(state.status) && (
        <>
          <div className="w-16 h-16 rounded-full bg-amber-100 mx-auto mb-6 flex items-center justify-center">
            {state.status === "error" || state.status === "failed" || state.status === "cancelled" ? (
              <XCircle size={48} weight="fill" className="text-red-600" />
            ) : (
              <Clock size={48} weight="fill" className="text-amber-600" />
            )}
          </div>
          <p className="text-xs tracking-[0.3em] uppercase text-amber-700 mb-2">
            {state.status === "error" || state.status === "failed" ? "Payment Failed" : "Payment Pending"}
          </p>
          <h1 className="font-serif text-3xl text-midnight mb-3">
            {state.status === "error" || state.status === "failed"
              ? "Something went wrong"
              : "Your payment is being processed"}
          </h1>
          <p className="text-sm text-neutral-600 mb-8">
            {state.status === "error" || state.status === "failed"
              ? "The payment could not be verified. If money was debited it will be refunded within 5-7 working days."
              : "Sometimes banks take a minute or two to confirm. Check your bookings page shortly."}
          </p>
          <Button onClick={goToBookings} variant="outline" className="border-midnight text-midnight hover:bg-stone px-8 py-6">
            Go to my {state.kind === "rental" ? "rentals" : "bookings"}
          </Button>
        </>
      )}
    </div>
  );
}
